import { useState, type FormEvent } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { AuthLayout } from '../../layouts/AuthLayout';
import { Button, Field, Notice } from '../../components/ui';
import { SocialButtons } from '../../components/SocialButtons';
import { authErrorMessage, signIn, submitSignInCode } from '../../lib/auth';
import { useSession } from '../../lib/session';
import { CONTACT_URL, useSelfServe } from '../../lib/signup';

export function SignInPage() {
  const selfServe = useSelfServe();
  const { refresh } = useSession();
  const navigate = useNavigate();
  const location = useLocation();
  const navState = location.state as { from?: string; reset?: boolean; verified?: boolean } | null;
  const from = navState?.from ?? '/';
  const justReset = !!navState?.reset;
  const justVerified = !!navState?.verified;

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [remember, setRemember] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [code, setCode] = useState('');
  const [codePrompt, setCodePrompt] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const finish = async () => {
    await refresh();
    navigate(from, { replace: true });
  };

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const step = codePrompt ? await submitSignInCode(code) : await signIn(email, password, remember);
      if (step.kind === 'done') await finish();
      else if (step.kind === 'code') setCodePrompt(step.message);
      else setError(step.message);
    } catch (err) {
      setError(authErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthLayout title="Sign in" subtitle="Use the same account as the EducAitor app.">
      {!codePrompt ? <SocialButtons /> : null}
      <form className="auth-form" onSubmit={onSubmit} noValidate>
        {error ? <Notice tone="error">{error}</Notice> : null}
        {justReset && !error ? <Notice tone="success">Password changed. Sign in with your new password.</Notice> : null}
        {justVerified && !error ? <Notice tone="success">Email confirmed. Sign in to finish setting up your school.</Notice> : null}
        {codePrompt ? (
          <>
            <Notice>{codePrompt}</Notice>
            <Field
              id="signin-code"
              label="Code"
              inputMode="numeric"
              autoComplete="one-time-code"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              autoFocus
            />
          </>
        ) : (
          <>
            <Field
              id="signin-email"
              label="Email"
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoFocus
            />
            <Field
              id="signin-password"
              label="Password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              trailing={
                <Button type="button" variant="ghost" onClick={() => setShowPassword((v) => !v)} aria-pressed={showPassword}>
                  {showPassword ? 'Hide' : 'Show'}
                </Button>
              }
            />
            <div className="auth-row">
              <label className="auth-check">
                <input id="signin-remember" type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} />
                Keep me signed in
              </label>
              <Link to="/forgot-password" state={{ email }}>
                Forgot password?
              </Link>
            </div>
            <p className="auth-hint">Don&rsquo;t tick &ldquo;Keep me signed in&rdquo; on a shared computer.</p>
          </>
        )}
        <Button type="submit" size="lg" block loading={busy}>
          {codePrompt ? 'Confirm' : 'Sign in'}
        </Button>
      </form>
      <p className="auth-foot">
        {selfServe ? (
          <>
            New to EducAitor? <Link to="/sign-up">Set up your organisation</Link>
          </>
        ) : (
          <>
            New to EducAitor?{' '}
            <a href={CONTACT_URL} target="_blank" rel="noreferrer">
              Get in touch
            </a>{' '}
            and we&rsquo;ll set up your organisation.
          </>
        )}
      </p>
    </AuthLayout>
  );
}
